/**
 * What a shift is, in wall-clock terms.
 *
 * Hanneza works 8pm to 4am PHT. Every part of that sentence is a problem for
 * software: it starts on one calendar day and ends on the next, it is stated in
 * her zone and stored in UTC, and the day it belongs to is the day it STARTED —
 * her Thursday night is Thursday's shift and must never file itself under
 * Friday.
 *
 * So a shift here is a start day, a start hour and a length, and this file
 * turns those into the two instants the database is asked for. Nothing in the
 * digest reasons about dates again after this.
 *
 * ── Why the offset is measured rather than assumed ─────────────────────────
 *
 * "8pm in Asia/Manila" is not a fixed number of hours from UTC everywhere in
 * the world or every month of the year. Manila has no daylight saving today,
 * but the Hub is used from three countries and the zone list changes a few
 * times a year, so hard-coding +08:00 would be right by luck. The offset is
 * read out of Intl at the instant in question, which is the only way to get it
 * right for a zone the code has never heard of.
 */

/** How far the zone is from UTC at that instant, in milliseconds. */
function offsetAt(instant: Date, timeZone: string): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      // h23, not hour12: false. The latter still renders midnight as "24" in
      // some engines, and Date.UTC(…, 24, …) silently rolls into the next day.
      hourCycle: 'h23',
    }).formatToParts(instant);
    const at = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? NaN);
    const asUtc = Date.UTC(
      at('year'),
      at('month') - 1,
      at('day'),
      at('hour'),
      at('minute'),
      at('second'),
    );
    if (Number.isNaN(asUtc)) return null;
    return asUtc - instant.getTime();
  } catch {
    // An unknown zone throws RangeError. Null rather than a guess: the caller
    // falls back to UTC and says so, which is a shift somebody can correct.
    return null;
  }
}

/**
 * The instant a wall-clock time in a zone actually happens.
 *
 * Two passes, because the offset depends on the answer: the first guess uses
 * the offset at the same clock reading in UTC, and the second re-reads it at
 * the instant that produced. They differ only across a daylight-saving edge,
 * which is exactly when one pass would be an hour wrong.
 */
function instantFor(
  day: { year: number; month: number; day: number },
  hour: number,
  timeZone: string,
): Date | null {
  const naive = Date.UTC(day.year, day.month - 1, day.day, hour);
  const first = offsetAt(new Date(naive), timeZone);
  if (first === null) return null;
  const once = naive - first;
  const second = offsetAt(new Date(once), timeZone);
  if (second === null) return null;
  return new Date(second === first ? once : naive - second);
}

export interface ShiftWindow {
  /** The first instant of the shift. */
  from: Date;
  /** The instant it ends — exclusive, so back-to-back shifts never double-count. */
  to: Date;
  /** The day the shift started, as YYYY-MM-DD. What it is filed under. */
  day: string;
  /** True when the zone could not be honoured and the window is UTC. */
  fellBackToUtc: boolean;
}

/** A whole number in range, or the fallback. Anything else is not a setting. */
function clampHour(value: number | null | undefined, low: number, high: number, fallback: number) {
  if (value === null || value === undefined || !Number.isFinite(value)) return fallback;
  const whole = Math.trunc(value);
  return whole < low || whole > high ? fallback : whole;
}

/**
 * The two instants that bound one person's shift on one day.
 *
 * `day` is the day the shift STARTS, YYYY-MM-DD, in the person's own zone. A
 * shift that runs past midnight simply ends on the next calendar day; there is
 * no second case for it, which is the whole reason this is start-plus-length
 * rather than a start and an end.
 */
export function shiftWindow(
  day: string,
  settings: { startHour: number; lengthHours: number; timeZone: string },
): ShiftWindow | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (match === null) return null;
  const parts = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
  const startHour = clampHour(settings.startHour, 0, 23, 9);
  const lengthHours = clampHour(settings.lengthHours, 1, 24, 8);

  const from = instantFor(parts, startHour, settings.timeZone);
  if (from === null) {
    const utc = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, startHour));
    return {
      from: utc,
      to: new Date(utc.getTime() + lengthHours * 3_600_000),
      day,
      fellBackToUtc: true,
    };
  }
  return {
    from,
    to: new Date(from.getTime() + lengthHours * 3_600_000),
    day,
    fellBackToUtc: false,
  };
}

/** Today in a zone, as YYYY-MM-DD. The day the shift picker opens on. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

/**
 * The shift a person is in the middle of, or the last one that ended.
 *
 * Opening the screen at 1am during a shift that began at 8pm yesterday should
 * show THAT shift, not an empty one that starts in nineteen hours. So: if
 * today's shift has not begun yet, the interesting one is yesterday's.
 */
export function currentShiftDay(
  settings: { startHour: number; lengthHours: number; timeZone: string },
  now: Date = new Date(),
): string {
  const today = todayIn(settings.timeZone, now);
  const window = shiftWindow(today, settings);
  if (window === null || now.getTime() >= window.from.getTime()) return today;
  return dayBefore(today);
}

/**
 * The calendar day before this one.
 *
 * Pure date arithmetic in UTC, deliberately: subtracting hours from an instant
 * and re-reading the zone lands on the wrong day whenever the gap between "now"
 * and the shift start is smaller than the number guessed — which is exactly the
 * 1am case this exists for.
 */
function dayBefore(day: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (match === null) return day;
  const at = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  at.setUTCDate(at.getUTCDate() - 1);
  return at.toISOString().slice(0, 10);
}

/**
 * A shift hour as a label in a dropdown: 9 → "9 AM", 20 → "8 PM".
 *
 * The hour is a plain 0-23 number in the person's OWN time zone
 * (profiles.shift_start_hour), so its label must not depend on any zone at
 * all. Formatting a UTC instant WITHOUT pinning the formatter to UTC reads it
 * in the browser's zone, and in Manila that turns the stored default of 9
 * into "5 PM" — which is exactly what Hanneza saw on 7 September, picked
 * "7 PM" to correct, and stored 11, because "7 PM" was the label sitting on
 * value 11. CI runs in UTC, where the two spellings agree, so no test caught
 * it. shiftHourLabel.test.ts now runs under Asia/Manila for that reason.
 */
export function hourLabel(hour: number): string {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(2026, 0, 1, hour)),
  );
}

/** A time as the person reads it on their own clock: "8:14 PM". */
export function clockTime(instant: Date, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
    }).format(instant);
  } catch {
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(instant);
  }
}

/** A shift's name in a sentence: "Thu, Sep 3 · 8:00 PM to 4:00 AM". */
export function shiftLabel(window: ShiftWindow, timeZone: string): string {
  const zone = window.fellBackToUtc ? 'UTC' : timeZone;
  let day: string;
  try {
    day = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(window.from);
  } catch {
    day = window.day;
  }
  return `${day} · ${clockTime(window.from, zone)} to ${clockTime(window.to, zone)}`;
}
